import type {
    KboUserAgreementDocument,
    KboUserAgreementStatus,
} from "components/KBo/Models/KboUserAgreement";
import BaseService, { ServiceError } from "./BaseService";

/**
 * Set on the 403 a KBo chat endpoint returns while the user still has to accept
 * the KBo User Agreement, to tell it apart from a missing permission.
 */
export const KboUserAgreementRequiredHeader = "Acx-Kbo-User-Agreement-Required";

export function isKboUserAgreementRequiredError(error: unknown) {
    return (
        error instanceof ServiceError &&
        error.response?.status === 403 &&
        error.response.headers
            .get(KboUserAgreementRequiredHeader)
            ?.toLowerCase() === "true"
    );
}

/**
 * The server turned the acceptance down, for example because a newer version
 * replaced it or the agreement no longer applies, rather than failing to save it.
 */
export function isKboUserAgreementAcceptRejected(error: unknown) {
    const status =
        error instanceof ServiceError ? error.response?.status : undefined;
    return status !== undefined && status >= 400 && status < 500;
}

export class KboUserAgreementService extends BaseService {
    protected urlBase = "api/KboUserAgreement";

    public async getStatus(): Promise<KboUserAgreementStatus> {
        return this.get<KboUserAgreementStatus>(new URLSearchParams(), "status");
    }

    public async accept(
        agreement: Pick<KboUserAgreementDocument, "version" | "revision">,
    ): Promise<KboUserAgreementStatus> {
        return this.post<KboUserAgreementStatus>(
            JSON.stringify({
                version: agreement.version,
                revision: agreement.revision,
            }),
            new URLSearchParams(),
            "accept",
            { returnJson: true },
        );
    }
}
